import React, { useState, useEffect } from "react";
import MetaConnectionRequirementsNotice from "../MetaConnectionRequirementsNotice";
import EmojiPicker from "../EmojiPicker";
import { sanitizeUrl } from "../../utils/security";

const FacebookForm = ({
  onChange,
  initialData = {},
  globalTitle,
  globalDescription,
  pages = [],
  onFileChange,
  currentFile,
  onReviewAI,
  onFindViralClips,
}) => {
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);

  useEffect(() => {
    if (currentFile && currentFile instanceof File) {
      const url = URL.createObjectURL(currentFile);
      setVideoPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setVideoPreviewUrl(null);
    }
  }, [currentFile]);

  const [pageId, setPageId] = useState(initialData.pageId || pages[0]?.id || "");

  // Fix for async data loading: If pageId is empty, try to select the first page when pages arrive
  useEffect(() => {
    if (!pageId && pages && pages.length > 0) {
      setPageId(pages[0].id);
    }
  }, [pages, pageId]);
  const [message, setMessage] = useState(initialData.message || globalDescription || "");
  const [isDirtyMessage, setIsDirtyMessage] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [postType, setPostType] = useState(initialData.postType || "feed"); // feed, story, reel
  const [isPaidPartnership, setIsPaidPartnership] = useState(
    initialData.isPaidPartnership || false
  );
  const [sponsorUser, setSponsorUser] = useState(initialData.sponsorUser || "");

  // Smart Sync: Update message when globalDescription changes, unless user has edited it
  useEffect(() => {
    if (!isDirtyMessage && globalDescription) {
      setMessage(globalDescription);
    }
  }, [globalDescription, isDirtyMessage]);

  useEffect(() => {
    // Determine the user-friendly page name
    const selectedPage = pages?.find(p => p.id === pageId);
    const pageName = selectedPage ? selectedPage.name : "";

    onChange({
      platform: "facebook",
      pageId,
      pageName, // Pass the name so UnifiedPublisher can show it in preview
      message,
      postType,
      isPaidPartnership,
      sponsorUser,
    });
  }, [pageId, message, postType, isPaidPartnership, sponsorUser, pages]);

  const handleInsertEmoji = emoji => {
    const emojiChar = typeof emoji === "object" && emoji.native ? emoji.native : emoji;
    setMessage(prev => prev + emojiChar);
    setIsDirtyMessage(true);
    setShowEmojiPicker(false);
  };

  return (
    <div className="platform-form facebook-form">
      <h4 className="platform-form-header">
        <span className="icon" style={{ color: "#1877F2" }}>
          f
        </span>{" "}
        Facebook Manager
      </h4>

      <MetaConnectionRequirementsNotice
        title="Facebook publishing requirements"
        pages={pages}
        style={{ marginBottom: "16px" }}
      />

      <div className="form-group-modern">
        <label className="form-label-bold">Media File</label>
        <div
          style={{
            fontSize: 12,
            color: "#666",
            marginBottom: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>
            {currentFile
              ? `Selected: ${currentFile.name}`
              : "Use global file or select unique file for Facebook"}
          </span>
          {currentFile && (
            <button
              type="button"
              onClick={() => onFileChange && onFileChange(null)}
              style={{
                background: "transparent",
                border: "1px solid #ef4444",
                color: "#ef4444",
                borderRadius: "4px",
                padding: "2px 8px",
                fontSize: "11px",
                cursor: "pointer",
              }}
            >
              Remove
            </button>
          )}
        </div>
        <input
          type="file"
          accept="video/*,image/*"
          onChange={e => onFileChange && onFileChange(e.target.files[0])}
          className="modern-input"
          style={{ padding: 8 }}
        />

        {/* --- REVIEW AI ENHANCEMENTS for Facebook --- */}
        {(onReviewAI || onFindViralClips) && (
          <div style={{ display: "flex", gap: "10px", marginTop: 8, marginBottom: 15 }}>
            {onReviewAI && (
              <button
                type="button"
                style={{
                  background: "linear-gradient(135deg, #1877F2 0%, #166FE5 100%)",
                  color: "white",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  flex: 1,
                }}
                onClick={onReviewAI}
              >
                ✨ Review AI Enhancements
              </button>
            )}
            {onFindViralClips && (
              <button
                type="button"
                style={{
                  background: "linear-gradient(135deg, #FF416C 0%, #FF4B2B 100%)",
                  color: "white",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  flex: 1,
                }}
                onClick={onFindViralClips}
              >
                🔥 Find Viral Clips
              </button>
            )}
          </div>
        )}

        {/* SIMPLE INLINE VIDEO PREVIEW */}
        {videoPreviewUrl && (currentFile?.type?.startsWith("video/") || !currentFile) && (
          <div style={{ marginTop: "10px" }}>
            <video
              src={sanitizeUrl(videoPreviewUrl)}
              controls
              style={{
                width: "100%",
                maxHeight: "300px",
                borderRadius: "8px",
                border: "1px solid #334155",
              }}
            />
          </div>
        )}
      </div>

      {pages.length === 0 ? (
        <div className="alert-box warning">
          No Facebook Pages connected. Please connect a page in settings.
        </div>
      ) : (
        <div className="form-group-modern">
          <label>Post As (Identity)</label>
          <select
            className="modern-select"
            value={pageId}
            onChange={e => setPageId(e.target.value)}
          >
            {pages.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} (ID: {p.id})
              </option>
            ))}
          </select>
          <p className="legal-hint" style={{ marginTop: "4px" }}>
            <span style={{ color: "#888" }}>Page ID: {pageId}</span>
          </p>

          <div
            className="scope-info-box"
            style={{
              marginTop: "8px",
              padding: "12px",
              background: "#f0f2f5",
              borderRadius: "8px",
              border: "1px solid #e1e3e8",
              fontSize: "0.85rem",
            }}
          >
            <div
              style={{
                fontWeight: "600",
                marginBottom: "6px",
                color: "#1877F2",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span>🔒</span> Data Access & Permissions
            </div>
            <p style={{ margin: 0, color: "#444", lineHeight: "1.4" }}>
              <strong>Why we need this:</strong>
            </p>
            <ul style={{ margin: "6px 0 0 20px", padding: 0, color: "#444", lineHeight: "1.4" }}>
              <li>
                <code
                  style={{
                    background: "#e4e6eb",
                    padding: "2px 4px",
                    borderRadius: "4px/2px",
                    fontFamily: "monospace",
                    color: "#333",
                  }}
                >
                  pages_show_list
                </code>
                : Used to display the list of Pages you manage in the dropdown above, so you can
                select where to post.
              </li>
              <li>
                <code
                  style={{
                    background: "#e4e6eb",
                    padding: "2px 4px",
                    borderRadius: "4px/2px",
                    fontFamily: "monospace",
                    color: "#333",
                  }}
                >
                  pages_manage_posts
                </code>
                : Used solely to publish this specific post to your selected Page timeline.
              </li>
              <li>
                <code
                  style={{
                    background: "#e4e6eb",
                    padding: "2px 4px",
                    borderRadius: "4px/2px",
                    fontFamily: "monospace",
                    color: "#333",
                  }}
                >
                  pages_read_engagement
                </code>
                : Used to display the likes and comments this post receives on your dashboard.
              </li>
              <li>
                <code
                  style={{
                    background: "#e4e6eb",
                    padding: "2px 4px",
                    borderRadius: "4px/2px",
                    fontFamily: "monospace",
                    color: "#333",
                  }}
                >
                  pages_manage_metadata
                </code>
                : Used to subscribe to page webhooks so we can track post performance automatically.
              </li>
            </ul>
            <p
              style={{
                margin: "8px 0 0 0",
                color: "#65676b",
                fontSize: "0.8rem",
                fontStyle: "italic",
                borderTop: "1px solid #e1e3e8",
                paddingTop: "6px",
              }}
            >
              <strong>User Privacy:</strong> We do not access your personal profile's private
              messages or modify your Page's admin settings.
            </p>
          </div>
        </div>
      )}

      <div className="form-group-modern">
        <label>Post Destination</label>
        <div className="card-selector">
          <div
            className={`card-option ${postType === "feed" ? "selected" : ""}`}
            onClick={() => setPostType("feed")}
          >
            <span className="emoji">📰</span>
            <span>News Feed</span>
          </div>
          <div
            className={`card-option ${postType === "reel" ? "selected" : ""}`}
            onClick={() => setPostType("reel")}
          >
            <span className="emoji">🎬</span>
            <span>Reels</span>
          </div>
          {/* Stories removed as they are not currently implemented in backend */}
        </div>
      </div>

      <div className="form-group-modern">
        <label>Post Text</label>
        <div style={{ position: "relative" }}>
          <textarea
            className="modern-input"
            value={message}
            onChange={e => {
              setMessage(e.target.value);
              setIsDirtyMessage(true);
            }}
            placeholder="What's on your mind?"
            rows={4}
            maxLength={63206}
          />
          <button
            type="button"
            style={{
              position: "absolute",
              right: "12px",
              top: "12px",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "1.2rem",
              opacity: 0.7,
            }}
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          >
            😊
          </button>
          {showEmojiPicker && (
            <div style={{ position: "absolute", zIndex: 10, top: "100%", right: 0 }}>
              <EmojiPicker
                onSelect={handleInsertEmoji}
                onClose={() => setShowEmojiPicker(false)}
              />
            </div>
          )}
        </div>
        <div
          className="char-count"
          style={{ textAlign: "right", fontSize: "0.76rem", opacity: 0.6, marginTop: "4px" }}
        >
          {message.length}/63,206
        </div>
      </div>

      <div className="commercial-section">
        <label className="checkbox-modern">
          <input
            type="checkbox"
            checked={isPaidPartnership}
            onChange={e => setIsPaidPartnership(e.target.checked)}
          />
          <span className="checkmark"></span>
          <span className="label-text">Tag Sponsor (Branded Content)</span>
        </label>

        {isPaidPartnership && (
          <div className="sub-settings fade-in">
            <div className="form-group-modern" style={{ marginTop: "10px" }}>
              <label>Sponsor Name / Page URL</label>
              <input
                type="text"
                className="modern-input"
                placeholder="e.g. Nike"
                value={sponsorUser}
                onChange={e => setSponsorUser(e.target.value)}
              />
            </div>
          </div>
        )}

        <p className="legal-hint">
          Required for paid partnerships. Handshake tool must be enabled on your Page.
        </p>
      </div>

      <div
        style={{
          fontSize: "0.85rem",
          color: "#6b7280",
          marginTop: "12px",
          padding: "8px",
          backgroundColor: "#f3f4f6",
          borderRadius: "4px",
          border: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <span>ℹ️</span>
        <span>Note: Video processing may take a few minutes to reflect on your Facebook Page.</span>
      </div>
    </div>
  );
};

export default FacebookForm;
